/**
 * Application Routes
 *
 * Centralized source of truth for all application route paths.
 * Uses PERMISSIONS from @/constants for type-safe permission references.
 *
 * Usage:
 * ```typescript
 * import { ROUTES } from '@/routes'
 *
 * <Link to={ROUTES.dashboard.root.path}>Dashboard</Link>
 * <Link to={ROUTES.billing.invoices.detail(123).path}>Invoice #123</Link>
 * ```
 */

import { PERMISSIONS, ROLES, type Role, type RoutePermission } from "@/constants"

// ==================== Type Definitions ====================

type IdParam = string | number

export interface RouteConfig {
  path: string
  title: string
  description: string
  isPublic?: boolean
  requiredPermissions?: RoutePermission[]
  requireAllPermissions?: boolean
  /**
   * Roles allowed to open this route. Evaluated by ProtectedRoute before permissions;
   * a user whose role is not listed is redirected to their own role home.
   * Omit for public routes.
   */
  allowedRoles?: Role[]
}

export type DynamicRouteConfig = (id: IdParam) => RouteConfig

// ==================== Shorthand ====================

const P = PERMISSIONS

// ==================== Route Definitions ====================

export const ROUTES = {
  // ==================== Public Routes ====================
  public: {
    home: {
      path: "/home",
      title: "CloudSpry",
      description: "AWS Cloud Cost Management Platform",
      isPublic: true,
    },
    login: {
      path: "/login",
      title: "Login",
      description: "Sign in to your CloudSpry account",
      isPublic: true,
    },
    authCallback: {
      path: "/auth/callback",
      title: "Authenticating",
      description: "Processing authentication...",
      isPublic: true,
    },
    register: {
      path: "/register",
      title: "Register",
      description: "Create your CloudSpry account",
      isPublic: true,
    },
    tiers: {
      path: "/tiers",
      title: "Tiers",
      description: "CloudSpry pricing plans and tiers",
      isPublic: true,
    },
    features: {
      path: "/features",
      title: "Features",
      description: "CloudSpry platform features and capabilities",
      isPublic: true,
    },
    about: {
      path: "/about",
      title: "About",
      description: "Our mission, team, and story",
      isPublic: true,
    },
    support: {
      path: "/support",
      title: "Support",
      description: "Help centre, documentation, and contact",
      isPublic: true,
    },
    forgotPassword: {
      path: "/forgot-password",
      title: "Forgot Password",
      description: "Request a password reset link",
      isPublic: true,
    },
  },

  // ==================== Dashboard ====================
  dashboard: {
    root: {
      path: "/dashboard",
      title: "Dashboard",
      description: "Overview of your AWS resources and costs",
      requiredPermissions: [P.DASHBOARD.VIEW],
      allowedRoles: [ROLES.SUPER_ADMIN],
    },
  },

  // ==================== Cloud Manager ====================
  cloudManager: {
    costAlert: {
      path: "/cloud-manager/cost-alert",
      title: "Cost Alert",
      description: "Monitor and manage cost alerts for your AWS resources",
      requiredPermissions: [P.COST_ALERTS.MANAGE],
      allowedRoles: [ROLES.SUPER_ADMIN],
    },
    performanceAlert: {
      path: "/cloud-manager/performance-alert",
      title: "Performance Alert",
      description: "Track performance metrics and receive alerts",
      requiredPermissions: [P.PERFORMANCE_ALERTS.MANAGE],
      allowedRoles: [ROLES.SUPER_ADMIN],
    },
    manageServer: {
      path: "/cloud-manager/manage-servers",
      title: "Manage Servers",
      description: "Control and monitor your servers",
      requiredPermissions: [P.INSTANCES.MANAGE],
      allowedRoles: [ROLES.SUPER_ADMIN],
    },
    reports: {
      path: "/cloud-manager/reports",
      title: "Reports",
      description: "Generate and download detailed reports",
      requiredPermissions: [P.REPORTS.VIEW],
      allowedRoles: [ROLES.SUPER_ADMIN],
    },
  },

  // ==================== Cloud Allocation ====================
  cloudAllocation: {
    departments: {
      path: "/cloud-allocation/departments",
      title: "Departments",
      description: "Manage departments and resource allocation",
      requiredPermissions: [P.DEPARTMENTS.MANAGE, P.DEPARTMENTS.VIEW],
      allowedRoles: [ROLES.SUPER_ADMIN],
    },
    members: {
      path: "/cloud-allocation/members",
      title: "Members",
      description: "Manage team members and department assignments",
      requiredPermissions: [P.MEMBERS.MANAGE, P.MEMBERS.VIEW],
      allowedRoles: [ROLES.SUPER_ADMIN],
    },
    unassigned: {
      path: "/cloud-allocation/unassigned",
      title: "Unassigned",
      description: "View and reassign members, admins, and resources with no department",
      requiredPermissions: [P.MEMBERS.MANAGE, P.MEMBERS.VIEW],
      allowedRoles: [ROLES.SUPER_ADMIN],
    },
  },

  // ==================== Cloud Advisor ====================
  cloudAdvisor: {
    cost: {
      path: "/cloud-advisor/cost-advisor",
      title: "Cost Advisor",
      description: "Comprehensive cost analysis and optimization recommendations",
      requiredPermissions: [P.COST_ADVISOR.VIEW],
      allowedRoles: [ROLES.SUPER_ADMIN],
    },
    performance: {
      path: "/cloud-advisor/performance-advisor",
      title: "Performance Advisor",
      description: "Real-time performance metrics and optimization recommendations",
      requiredPermissions: [P.PERFORMANCE_ADVISOR.VIEW],
      allowedRoles: [ROLES.SUPER_ADMIN],
    },
    security: {
      path: "/security-advisor",
      title: "Security Advisor",
      description: "Security findings, vulnerabilities, and compliance recommendations",
      requiredPermissions: [P.SECURITY_ADVISOR.VIEW],
      allowedRoles: [ROLES.SUPER_ADMIN],
    },
  },

  // ==================== Billing ====================
  billing: {
    costUsage: {
      path: "/billing/cost-and-usage",
      title: "Cost and Usage",
      description: "View AWS cost and usage summary for the current and previous month",
      requiredPermissions: [P.BILLING.VIEW],
      allowedRoles: [ROLES.SUPER_ADMIN],
    },
    alerts: {
      path: "/billing/alerts",
      title: "Billing Alerts",
      description: "Monitor and manage billing alerts",
      requiredPermissions: [P.BILLING.MANAGE, P.BILLING.VIEW],
      allowedRoles: [ROLES.SUPER_ADMIN],
    },
    // awsAccounts: {
    //   path: "/billing/aws-accounts",
    //   title: "AWS Accounts",
    //   description: "Manage your connected AWS accounts",
    //   requiredPermissions: [P.BILLING.MANAGE, P.BILLING.VIEW],
    // },
    // invoices: {
    //   list: {
    //     path: "/billing/invoices",
    //     title: "Invoices",
    //     description: "View and manage your billing invoices",
    //     requiredPermissions: [P.BILLING.MANAGE, P.BILLING.VIEW],
    //   },
    //   detail: (id: IdParam): RouteConfig => ({
    //     path: `/billing/invoices/${id}`,
    //     title: `Invoice #${id}`,
    //     description: "View invoice details",
    //     requiredPermissions: [P.BILLING.MANAGE, P.BILLING.VIEW],
    //   }),
    // },
    // payments: {
    //   path: "/billing/payments",
    //   title: "Payments",
    //   description: "View your payment history and transactions",
    //   requiredPermissions: [P.BILLING.VIEW],
    // },
    // snapshots: {
    //   path: "/billing/snapshots",
    //   title: "Billing Snapshots",
    //   description: "View detailed cost breakdowns",
    //   requiredPermissions: [P.BILLING.VIEW],
    // },
  },

  // ==================== Notifications ====================
  notifications: {
    list: {
      path: "/notifications",
      title: "Notifications",
      description: "View all your notifications",
      requiredPermissions: [P.ACCOUNT.VIEW],
      allowedRoles: [ROLES.SUPER_ADMIN],
    },
  },

  // ==================== Account & Settings ====================
  account: {
    myAccount: {
      path: "/my-account",
      title: "My Account",
      description: "Manage your account and payment information",
      requiredPermissions: [P.ACCOUNT.VIEW],
      allowedRoles: [ROLES.SUPER_ADMIN],
    },
    settings: {
      path: "/settings",
      title: "Settings",
      description: "User preferences and AWS account configuration",
      requiredPermissions: [P.SETTINGS.VIEW],
      allowedRoles: [ROLES.SUPER_ADMIN],
    },
  },
  // ==================== Legacy redirect-only paths (no page) ====================
  // Paths that backend emails link to but that have no page. They exist only so
  // <RoleAwareRedirect /> can send the signed-in user to their own home.
  legacy: {
    billingRoot: {
      path: "/billing",
      title: "Billing",
      description: "Redirects to your home page",
    },
  },

  // ==================== Department Admin (role: admin) ====================
  deptAdmin: {
    dashboard: {
      path: "/dept-admin/dashboard",
      title: "Dashboard",
      description: "Overview of your department",
      requiredPermissions: [P.DASHBOARD.VIEW],
      allowedRoles: [ROLES.ADMIN],
    },
    department: {
      path: "/dept-admin/department",
      title: "My Department",
      description: "Manage your department's settings, members and servers",
      requiredPermissions: [P.DEPARTMENTS.VIEW],
      allowedRoles: [ROLES.ADMIN],
    },
    members: {
      path: "/dept-admin/members",
      title: "Members",
      description: "Manage the members of your department",
      requiredPermissions: [P.MEMBERS.VIEW],
      allowedRoles: [ROLES.ADMIN],
    },
    notifications: {
      path: "/dept-admin/notifications",
      title: "Notifications",
      description: "View all your notifications",
      requiredPermissions: [P.ACCOUNT.VIEW],
      allowedRoles: [ROLES.ADMIN],
    },
    account: {
      path: "/dept-admin/account",
      title: "My Account",
      description: "Manage your profile",
      requiredPermissions: [P.ACCOUNT.VIEW],
      allowedRoles: [ROLES.ADMIN],
    },
    settings: {
      path: "/dept-admin/settings",
      title: "Settings",
      description: "Notification, security and preference settings",
      requiredPermissions: [P.SETTINGS.VIEW],
      allowedRoles: [ROLES.ADMIN],
    },
  },

  // ==================== Department Member (role: member) ====================
  deptMember: {
    dashboard: {
      path: "/dept-member/dashboard",
      title: "Dashboard",
      description: "Overview of your department",
      requiredPermissions: [P.DASHBOARD.VIEW],
      allowedRoles: [ROLES.MEMBER],
    },
    department: {
      path: "/dept-member/department",
      title: "My Department",
      description: "View your department, its members and servers",
      requiredPermissions: [P.DEPARTMENTS.VIEW],
      allowedRoles: [ROLES.MEMBER],
    },
    notifications: {
      path: "/dept-member/notifications",
      title: "Notifications",
      description: "View all your notifications",
      requiredPermissions: [P.ACCOUNT.VIEW],
      allowedRoles: [ROLES.MEMBER],
    },
    account: {
      path: "/dept-member/account",
      title: "My Account",
      description: "Manage your profile",
      requiredPermissions: [P.ACCOUNT.VIEW],
      allowedRoles: [ROLES.MEMBER],
    },
    settings: {
      path: "/dept-member/settings",
      title: "Settings",
      description: "Notification, security and preference settings",
      requiredPermissions: [P.SETTINGS.VIEW],
      allowedRoles: [ROLES.MEMBER],
    },
  },
} as const

// ==================== Type Exports ====================

export type AppRoutes = typeof ROUTES
